// ────────────────────────────────────────────────────────────────────
// Register import: map hierarchy columns → create-only commit.
//
// Create-only on purpose — an asset that already exists is SKIPPED and its
// differences are reported, never overwritten. The mapping is pre-filled from
// the same keyword sets the console classified the sheet with.
// ────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { X, CheckCircle2, ChevronDown, ChevronUp } from "lucide-react";
import { apiClient } from "@/lib/api";
import { seedRegisterMapping } from "@/lib/importClassify";
import { PickerNote } from "@/components/intake/primitives";
import { RejectsBlock, REGISTER_REJECT_COLS } from "@/screens/intake/importShared";
import type { IntakeFile, RegisterMapping, RegisterPreview } from "@/lib/types";

type RegisterMapKey = Exclude<keyof RegisterMapping, "sheet" | "header_row">;
const REGISTER_OPTIONAL_FIELDS: readonly { label: string; k: RegisterMapKey }[] = [
  { label: "Name", k: "name" }, { label: "Class", k: "asset_class" },
  { label: "Design code", k: "design_code" }, { label: "Service", k: "service" },
  { label: "Material", k: "material" }, { label: "Corrosion loop", k: "corrosion_loop" },
  { label: "Damage mech", k: "damage_mechanism" }, { label: "In-service date", k: "first_service_date" },
  { label: "Drawing no", k: "drawing_no" },
];
export function RegisterImportModal({ file, onClose }: { file: IntakeFile; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const inspectQ = useQuery({ queryKey: ["intake-inspect", file.id], queryFn: () => apiClient.intakeInspect(file.id), staleTime: 60_000 });
  const cols = inspectQ.data?.columns ?? [];
  const [map, setMap] = useState<RegisterMapping>({ plant: "", area: "", asset_tag: "" });
  const [more, setMore] = useState(false);
  // Same pre-fill the thickness modal gets, from the same keyword sets. Without
  // this the register mapping opened blank and the user re-picked three columns
  // the console had already read one screen earlier to call the sheet a
  // register at all.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!seeded && cols.length) {
      setMap((m) => ({ ...m, ...seedRegisterMapping(cols) }));
      setSeeded(true);
    }
  }, [cols, seeded]);

  const preview = useMutation({ mutationFn: (m: RegisterMapping) => apiClient.intakeRegisterPreview(file.id, m) });
  const commit = useMutation({ mutationFn: () => apiClient.intakeRegisterCommit(file.id, map),
    onSuccess: () => {
      // The new hierarchy must show up everywhere it's cached: the intake
      // folder tree + every plant/area/asset list (SiteMap, Atlas). The
      // file-to-asset picker uses ["intake-asset-search", q] — NOT covered
      // here; its 15s staleTime bounds the lag for a just-created asset.
      void qc.invalidateQueries({ queryKey: ["intake"] });
      void qc.invalidateQueries({ queryKey: ["plants"] });
      void qc.invalidateQueries({ queryKey: ["areas"] });
      void qc.invalidateQueries({ queryKey: ["assets"] });
    } });
  const ready = Boolean(map.plant && map.area && map.asset_tag);
  const r: RegisterPreview | undefined = preview.data;
  const done = commit.data;

  const Field = ({ label, k, optional }: { label: string; k: RegisterMapKey; optional?: boolean }) => (
    <label className="flex items-center gap-2">
      <span className="font-mono uppercase w-24 shrink-0" style={{ fontSize: 9, letterSpacing: ".1em", color: optional ? "var(--ink-4)" : "var(--ink-2)" }}>{label}</span>
      <select value={map[k] ?? ""} onChange={(e) => { setMap((m) => ({ ...m, [k]: e.target.value || (optional ? undefined : "") })); preview.reset(); }}
              className="flex-1 font-mono px-2 py-1 outline-none" style={{ fontSize: 12, color: "var(--ink-1)", background: "var(--paper-2)", border: "1px solid var(--hairline)" }}>
        <option value="">{optional ? "— none —" : "— pick column —"}</option>
        {cols.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    </label>
  );

  return (
    <div className="fixed inset-0 z-[1200] flex items-start justify-center pt-[9vh]" style={{ background: "rgba(10,15,24,0.5)" }} onClick={onClose}>
      <div className="w-[560px] max-w-[94vw] shadow-2xl max-h-[82vh] overflow-auto" onClick={(e) => e.stopPropagation()}
           style={{ background: "var(--paper)", border: "1px solid var(--hairline)" }}>
        <div className="px-4 py-3 border-b flex items-center justify-between sticky top-0" style={{ borderColor: "var(--hairline)", background: "var(--paper)" }}>
          <div className="font-mono uppercase truncate" style={{ fontSize: 10, letterSpacing: ".16em", color: "var(--ink-3)" }}>
            / IMPORT ASSET REGISTER · <span style={{ color: "var(--ink-1)" }}>{file.filename}</span>
          </div>
          <button onClick={onClose}><X size={14} style={{ color: "var(--ink-3)" }} /></button>
        </div>

        <div className="p-4">
          {inspectQ.isPending && <PickerNote>Reading file…</PickerNote>}
          {inspectQ.isError && <PickerNote>Could not read this file.</PickerNote>}
          {cols.length > 0 && (
            <>
              <div className="font-mono uppercase mb-2" style={{ fontSize: 9, letterSpacing: ".18em", color: "var(--ink-4)" }}>Map columns</div>
              <div className="flex flex-col gap-1.5">
                <Field label="Plant" k="plant" />
                <Field label="Area" k="area" />
                <Field label="Asset tag" k="asset_tag" />
                {more && REGISTER_OPTIONAL_FIELDS.map((f) => <Field key={f.k} label={f.label} k={f.k} optional />)}
              </div>
              <button onClick={() => setMore((v) => !v)}
                      className="mt-1.5 font-mono uppercase transition hover:opacity-70 inline-flex items-center gap-1"
                      style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--ink-3)" }}>
                {more ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                {more ? "Fewer fields" : `More fields (${REGISTER_OPTIONAL_FIELDS.length})`}
              </button>

              <button onClick={() => { commit.reset(); preview.mutate(map); }}
                      disabled={!ready || preview.isPending}
                      className="mt-3 font-mono uppercase w-full py-2 border transition hover:bg-[rgba(var(--accent-rgb)_/_0.08)] active:translate-y-px disabled:opacity-40"
                      style={{ fontSize: 10, letterSpacing: ".12em", color: "var(--accent-deep)", borderColor: "var(--accent)", fontWeight: 700 }}>
                {preview.isPending ? "Checking…" : "Preview register"}
              </button>

              {r && (
                <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--hairline)" }}>
                  <div className="font-mono mb-2 px-3 py-2" style={{ fontSize: 11, color: "var(--ink-2)", background: "var(--paper-2)", border: "1px solid var(--hairline)" }}>
                    Creates {r.new_plants.length} plants · {r.new_areas.length} areas · {r.new_assets} assets — skips {r.existing.length} existing
                  </div>
                  {Boolean(r.data_quality.rejects?.length) && (
                    <RejectsBlock rejects={r.data_quality.rejects} filename={file.filename} columns={REGISTER_REJECT_COLS} />
                  )}
                  {r.existing.length > 0 && (
                    <div className="mb-2 px-3 py-2" style={{ background: "var(--paper-2)", border: "1px solid var(--hairline)" }}>
                      <div className="font-mono uppercase" style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--ink-3)", fontWeight: 700 }}>
                        {r.existing.length} already exist — skipped (create-only; in-app corrections are never overwritten)
                      </div>
                      <table className="w-full font-mono mt-1" style={{ fontSize: 10 }}>
                        <tbody>
                          {r.existing.slice(0, 8).map((x) => (
                            <tr key={`${x.plant}/${x.area}/${x.tag}`} style={{ verticalAlign: "top" }}>
                              <td style={{ color: "var(--ink-1)" }}>{x.tag}</td>
                              <td style={{ color: "var(--ink-3)" }}>{x.plant} / {x.area}</td>
                              <td style={{ color: "var(--ink-3)" }}>
                                {Object.entries(x.diffs).map(([f, d]) => (
                                  <div key={f}>{f}: {d.current || "—"} → {d.incoming}</div>
                                ))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {r.existing.length > 8 && (
                        <div className="font-mono" style={{ fontSize: 9, color: "var(--ink-3)" }}>+{r.existing.length - 8} more</div>
                      )}
                    </div>
                  )}
                  {done ? (
                    <div className="mt-3 font-mono px-3 py-2.5 flex items-center gap-1.5"
                         style={{ fontSize: 11, color: "var(--ok)", background: "rgba(21,128,61,.07)", border: "1px solid rgba(21,128,61,.4)" }}>
                      <CheckCircle2 size={14} /> Created {done.plants_created} plants · {done.areas_created} areas · {done.assets_created} assets — skipped {done.assets_skipped} existing
                    </div>
                  ) : (
                    <>
                      <div className="mt-3 font-mono px-3 py-2" style={{ fontSize: 10, color: "var(--ink-4)", background: "var(--paper-2)", border: "1px solid var(--hairline)" }}>
                        Create-only: new plants/areas/assets are added; existing assets are skipped untouched · หลักการ "ไม่เดา"
                      </div>
                      {r.data_quality.rows_ok > 0 && (
                        <button onClick={() => commit.mutate()} disabled={commit.isPending}
                                className="mt-2 font-mono uppercase w-full py-2 border transition hover:brightness-95 active:translate-y-px disabled:opacity-40"
                                style={{ fontSize: 10, letterSpacing: ".12em", color: "#fff", background: "var(--ok)", borderColor: "var(--ok)", fontWeight: 700 }}>
                          {commit.isPending ? "Creating…" : `Create register — ${r.new_assets} assets`}
                        </button>
                      )}
                      {commit.isError && (
                        <div className="mt-2 font-mono" style={{ fontSize: 11, color: "var(--err)" }}>Import failed — nothing was written. Check the mapping.</div>
                      )}
                    </>
                  )}
                </div>
              )}
              {preview.isError && <div className="mt-2 font-mono" style={{ fontSize: 11, color: "var(--err)" }}>Could not parse with this mapping — check the columns.</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

